import { showError } from "../../../../util/ShowStatus";

const formatFilterDate = (date: Date | null): string | null => {
  if (!date) return null;
  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
  )
    .toISOString()
    .split("T")[0]; // Format as 'YYYY-MM-DD'
};

// Ensure the input is a valid Date object
const ensureDate = (value: any): Date => {
  if (value instanceof Date) {
    return new Date(
      Date.UTC(
        value.getUTCFullYear(),
        value.getUTCMonth(),
        value.getUTCDate(),
        value.getUTCHours(),
        value.getUTCMinutes(),
        value.getUTCSeconds(),
        value.getUTCMilliseconds()
      )
    );
  }

  // If it's a string or number, try to create a Date from it
  if (typeof value === "string" || typeof value === "number") {
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      return new Date(
        Date.UTC(
          date.getUTCFullYear(),
          date.getUTCMonth(),
          date.getUTCDate(),
          date.getUTCHours(),
          date.getUTCMinutes(),
          date.getUTCSeconds(),
          date.getUTCMilliseconds()
        )
      );
    }
  }

  // Default to current date if invalid
  console.warn("Invalid date value, using current date instead", value);
  const now = new Date();
  return new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      now.getUTCHours(),
      now.getUTCMinutes(),
      now.getUTCSeconds(),
      now.getUTCMilliseconds()
    )
  );
};

// Custom function to get the start of week (Sunday, UTC) for any given date
const getStartOfWeek = (input: Date | null): Date => {
  if (!input) return ensureDate(new Date());
  const date = ensureDate(input);
  const dayOfWeek = date.getUTCDay(); // 0 for Sunday, 1 for Monday, etc.
  const diff = date.getUTCDate() - dayOfWeek;
  const startOfWeek = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), diff)
  );
  startOfWeek.setUTCHours(0, 0, 0, 0); // Set to beginning of the day UTC
  return startOfWeek;
};

// Custom function to get the end of week (Saturday, UTC) for any given date
const getEndOfWeek = (input: Date | null): Date => {
  const startOfWeek = getStartOfWeek(input);
  const endOfWeek = new Date(startOfWeek.getTime() + 6 * 24 * 60 * 60 * 1000);
  endOfWeek.setUTCHours(23, 59, 59, 999); // Set to end of the day UTC
  return endOfWeek;
};

// Custom function to add days to a date (UTC)
const addDays = (date: Date, days: number): Date => {
  const utcDate = ensureDate(date);
  const result = new Date(utcDate.getTime() + days * 24 * 60 * 60 * 1000);
  return result;
};

// Custom function to check if two dates are in the same week (UTC)
const isSameWeek = (date1: any, date2: any): boolean => {
  const d1 = ensureDate(date1);
  const d2 = ensureDate(date2);

  const startOfWeek1 = getStartOfWeek(d1);
  const startOfWeek2 = getStartOfWeek(d2);

  return startOfWeek1.getTime() === startOfWeek2.getTime();
};

// Custom function to check if a date is after another date (UTC)
const isAfter = (date1: any, date2: any): boolean => {
  const d1 = ensureDate(date1);
  const d2 = ensureDate(date2);
  return d1.getTime() > d2.getTime();
};

// Custom function to check if a date is before another date (UTC)
const isBefore = (date1: any, date2: any): boolean => {
  const d1 = ensureDate(date1);
  const d2 = ensureDate(date2);
  return d1.getTime() < d2.getTime();
};

// Main function to handle week selection
export const handleWeekChange = (props: any) => {
  // If the value is null or undefined, clear the selection
  if (!props.value) {
    props.setExtendTimeSheetWeek(null);
    props.setExtendTimeSheetData((prev: any) => ({
      ...prev,
      startDate: null,
      endDate: null,
    }));
    return;
  }

  let date;

  // Handle PrimeReact Calendar's specific value structure
  // It sometimes returns an array or an object with a value property
  if (Array.isArray(props.value)) {
    // If it's already an array with valid dates, check if first date is valid
    date = props.value[0] ? ensureDate(props.value[0]) : ensureDate(new Date());
  } else if (
    props.value &&
    typeof props.value === "object" &&
    "value" in props.value
  ) {
    // Some components might wrap the date in an event object
    date = ensureDate(props.value.value);
  } else {
    // Otherwise, try to use the value directly
    date = ensureDate(props.value);
  }

  const today = ensureDate(new Date());

  // Get current week's range (Sunday to Saturday, UTC)
  const currentWeekStart = getStartOfWeek(today);
  const currentWeekEnd = getEndOfWeek(today);

  // Get next week's range
  const nextWeekStart = addDays(currentWeekStart, 7);
  const nextWeekEnd = addDays(currentWeekEnd, 7);

  // Check if selected date is within current or next week
  const isInCurrentWeek = isSameWeek(date, today);
  const isInNextWeek = isSameWeek(date, nextWeekStart);

  if (isInCurrentWeek) {
    // If date is in current week, select current week's range
    props.setExtendTimeSheetWeek([currentWeekStart, currentWeekEnd]);
    props.setExtendTimeSheetData((prev: any) => ({
      ...prev,
      startDate: currentWeekStart,
      endDate: currentWeekEnd,
    }));
  } else if (isInNextWeek) {
    // If date is in next week, select next week's range
    props.setExtendTimeSheetWeek([nextWeekStart, nextWeekEnd]);
    props.setExtendTimeSheetData((prev: any) => ({
      ...prev,
      startDate: nextWeekStart,
      endDate: nextWeekEnd,
    }));
  } else if (isAfter(date, nextWeekEnd) || isBefore(date, currentWeekStart)) {
    // If date is outside allowed range (not in current or next week)
    // Default to current week
    showError(
      "Date selection outside of allowed range (current week or next week)",
      props.toast
    );
    props.setExtendTimeSheetWeek([currentWeekStart, currentWeekEnd]);
    props.setExtendTimeSheetData((prev: any) => ({
      ...prev,
      startDate: currentWeekStart,
      endDate: currentWeekEnd,
    }));
  }
};
